export type PermissionActionDefinition = {
  name: string;
  description: string;
};

export type PermissionResourceDefinition = {
  name: string;
  code: string;
  type: 'MENU';
  description: string;
  actions: PermissionActionDefinition[];
};

const action = (
  name: string,
  description: string,
): PermissionActionDefinition => ({
  name,
  description,
});

const baseActions = [action('view', '查看'), action('query', '查询')];

export const ATTENDANCE_CENTER_PERMISSION_RESOURCES = [
  {
    name: '我的考勤概览',
    code: 'attendance-center-my-overview',
    type: 'MENU',
    description: '控制我的考勤概览页面访问和下钻',
    actions: [...baseActions, action('open-drilldown', '打开下钻')],
  },
  {
    name: '我的打卡记录',
    code: 'attendance-center-my-records',
    type: 'MENU',
    description: '控制我的打卡记录页面访问和查询',
    actions: baseActions,
  },
  {
    name: '我的考勤申请',
    code: 'attendance-center-my-requests',
    type: 'MENU',
    description: '控制我的考勤申请页面访问和提交',
    actions: [
      ...baseActions,
      action('create-leave', '新建请假'),
      action('create-overtime', '新建加班'),
      action('create-punch', '新建补卡'),
      action('submit-request', '提交申请'),
      action('cancel-request', '取消申请'),
    ],
  },
  {
    name: '我的假期余额',
    code: 'attendance-center-my-leave-balances',
    type: 'MENU',
    description: '控制我的假期余额页面访问和查询',
    actions: baseActions,
  },
  {
    name: '我的考勤月报',
    code: 'attendance-center-my-monthly',
    type: 'MENU',
    description: '控制我的考勤月报页面访问和下载',
    actions: [...baseActions, action('download-monthly', '下载月报')],
  },
  {
    name: '团队考勤概览',
    code: 'attendance-center-team-overview',
    type: 'MENU',
    description: '控制团队考勤概览页面访问和下钻',
    actions: [...baseActions, action('open-drilldown', '打开下钻')],
  },
  {
    name: '团队排班',
    code: 'attendance-center-team-schedules',
    type: 'MENU',
    description: '控制团队排班页面访问和导出',
    actions: [...baseActions, action('export-schedules', '导出排班')],
  },
  {
    name: '团队异常',
    code: 'attendance-center-team-exceptions',
    type: 'MENU',
    description: '控制团队异常页面访问和处理',
    actions: [
      ...baseActions,
      action('handle-exception', '处理异常'),
      action('ignore-exception', '忽略异常'),
      action('export-exceptions', '导出异常'),
    ],
  },
  {
    name: '团队考勤月报',
    code: 'attendance-center-team-monthly',
    type: 'MENU',
    description: '控制团队考勤月报页面访问和导出',
    actions: [...baseActions, action('export-monthly', '导出月报')],
  },
  {
    name: '考勤工作台',
    code: 'attendance-center-workbench',
    type: 'MENU',
    description: '控制考勤工作台页面访问、待办入口和风险下钻',
    actions: [
      ...baseActions,
      action('open-todo', '打开待办'),
      action('open-risk-drilldown', '打开风险下钻'),
    ],
  },
  {
    name: '我的审批',
    code: 'attendance-center-approval',
    type: 'MENU',
    description: '控制考勤中心我的审批页面访问、审批任务查询和业务单据回跳',
    actions: [...baseActions, action('open-source', '回跳业务单据')],
  },
  {
    name: '打卡记录',
    code: 'attendance-center-records',
    type: 'MENU',
    description: '控制打卡记录页面访问、补录、导入和导出',
    actions: [
      ...baseActions,
      action('create-record', '补录打卡'),
      action('import-records', '导入打卡'),
      action('export-records', '导出打卡'),
    ],
  },
  {
    name: '每日考勤',
    code: 'attendance-center-daily-results',
    type: 'MENU',
    description: '控制每日考勤页面访问和异常处理',
    actions: [
      ...baseActions,
      action('calculate-daily', '计算日结果'),
      action('view-detail', '查看详情'),
      action('adjust-result', '调整日结果'),
      action('revoke-adjustment', '撤销人工调整'),
      action('export-daily', '导出日结果'),
    ],
  },
  {
    name: '每月考勤',
    code: 'attendance-center-monthly-summaries',
    type: 'MENU',
    description: '控制每月考勤页面访问和锁定操作',
    actions: [
      ...baseActions,
      action('generate-monthly', '生成月汇总'),
      action('lock-monthly', '锁定考勤'),
      action('unlock-monthly', '解除锁定'),
      action('view-detail', '查看详情'),
      action('export-monthly', '导出月汇总'),
    ],
  },
  {
    name: '考勤操作审计',
    code: 'attendance-center-operation-audits',
    type: 'MENU',
    description: '控制考勤操作审计页面访问和导出',
    actions: [
      ...baseActions,
      action('view-detail', '查看详情'),
      action('export-audits', '导出审计'),
    ],
  },
  {
    name: '请假申请',
    code: 'attendance-center-leave-requests',
    type: 'MENU',
    description: '控制请假申请页面访问和流程操作',
    actions: [
      ...baseActions,
      action('create-leave', '新建请假'),
      action('submit-leave', '提交请假'),
      action('approve-request', '审批申请'),
      action('reject-request', '驳回申请'),
      action('cancel-request', '取消申请'),
      action('export-leave', '导出请假'),
    ],
  },
  {
    name: '加班申请',
    code: 'attendance-center-overtime-requests',
    type: 'MENU',
    description: '控制加班申请页面访问和流程操作',
    actions: [
      ...baseActions,
      action('create-overtime', '新建加班'),
      action('submit-overtime', '提交加班'),
      action('approve-request', '审批申请'),
      action('reject-request', '驳回申请'),
      action('cancel-request', '取消申请'),
      action('export-overtime', '导出加班'),
    ],
  },
  {
    name: '补卡申请',
    code: 'attendance-center-punch-requests',
    type: 'MENU',
    description: '控制补卡申请页面访问和流程操作',
    actions: [
      ...baseActions,
      action('create-punch', '新建补卡'),
      action('submit-punch', '提交补卡'),
      action('approve-request', '审批申请'),
      action('reject-request', '驳回申请'),
      action('cancel-request', '取消申请'),
      action('export-punch', '导出补卡'),
    ],
  },
  {
    name: '班次管理',
    code: 'attendance-center-shifts',
    type: 'MENU',
    description: '控制班次管理页面访问和班次维护',
    actions: [
      ...baseActions,
      action('create-shift', '新增班次'),
      action('edit-shift', '编辑班次'),
      action('publish-shift', '发布班次'),
      action('disable-shift', '停用班次'),
      action('delete-shift', '删除班次'),
      action('export-shifts', '导出班次'),
    ],
  },
  {
    name: '考勤规则',
    code: 'attendance-center-rules',
    type: 'MENU',
    description: '控制考勤规则页面访问和规则维护',
    actions: [
      ...baseActions,
      action('create-rule', '新增规则'),
      action('edit-rule', '编辑规则'),
      action('publish-rule', '发布规则'),
      action('disable-rule', '停用规则'),
      action('delete-rule', '删除规则'),
      action('export-rules', '导出规则'),
    ],
  },
  {
    name: '考勤组',
    code: 'attendance-center-groups',
    type: 'MENU',
    description: '控制考勤组页面访问、成员维护和启停',
    actions: [
      ...baseActions,
      action('create-group', '新增考勤组'),
      action('edit-group', '编辑考勤组'),
      action('activate-group', '启用考勤组'),
      action('disable-group', '停用考勤组'),
      action('delete-group', '删除考勤组'),
      action('manage-group-members', '维护成员'),
      action('disable-group-member', '停用成员'),
      action('export-groups', '导出考勤组'),
    ],
  },
  {
    name: '排班日历',
    code: 'attendance-center-schedules',
    type: 'MENU',
    description: '控制排班日历页面访问、排班维护和批量生成',
    actions: [
      ...baseActions,
      action('create-schedule', '新增排班'),
      action('edit-schedule', '编辑排班'),
      action('publish-schedule', '发布排班'),
      action('disable-schedule', '停用排班'),
      action('delete-schedule', '删除排班'),
      action('batch-generate-schedules', '批量生成排班'),
      action('export-schedules', '导出排班'),
    ],
  },
  {
    name: '节假日安排',
    code: 'attendance-center-holiday-plans',
    type: 'MENU',
    description: '控制节假日安排页面访问和方案维护',
    actions: [
      ...baseActions,
      action('create-holiday-plan', '新增节假日安排'),
      action('edit-holiday-plan', '编辑节假日安排'),
      action('publish-holiday-plan', '发布节假日安排'),
      action('disable-holiday-plan', '停用节假日安排'),
      action('delete-holiday-plan', '删除节假日安排'),
      action('edit-holiday-days', '维护假期日期'),
      action('export-holiday-plans', '导出节假日安排'),
    ],
  },
  {
    name: '员工假期',
    code: 'attendance-center-leave-balances',
    type: 'MENU',
    description: '控制员工假期查询、额度发放与调整',
    actions: [
      ...baseActions,
      action('create-balance', '新增余额'),
      action('edit-balance', '调整额度'),
      action('grant-balance', '发放额度'),
      action('carry-over-balance', '年度结转'),
      action('export-balances', '导出余额'),
    ],
  },
  {
    name: '假期发放规则',
    code: 'attendance-center-leave-grant-policies',
    type: 'MENU',
    description: '控制假期发放规则页面访问和规则执行',
    actions: [
      ...baseActions,
      action('create-policy', '新增发放规则'),
      action('edit-policy', '编辑发放规则'),
      action('publish-policy', '发布发放规则'),
      action('disable-policy', '停用发放规则'),
      action('delete-policy', '删除发放规则'),
      action('apply-policy', '执行发放规则'),
      action('export-policies', '导出发放规则'),
    ],
  },
  {
    name: '考勤设置',
    code: 'attendance-center-settings',
    type: 'MENU',
    description: '控制考勤设置页面访问、保存和设备配置',
    actions: [
      ...baseActions,
      action('save-settings', '保存设置'),
      action('import-settings', '导入设置'),
      action('export-settings', '导出设置'),
      action('save-device-config', '保存设备配置'),
    ],
  },
  {
    name: '审批流程设置',
    code: 'attendance-center-workflow-settings',
    type: 'MENU',
    description: '控制考勤审批流程设置页面访问和流程启停保存',
    actions: [
      ...baseActions,
      action('configure-workflow', '设置审批流程'),
      action('save-workflow-settings', '保存审批流程设置'),
    ],
  },
] as const satisfies readonly PermissionResourceDefinition[];

export const PERMISSION_RESOURCE_CODES = {
  myOverview: 'attendance-center-my-overview',
  myRecords: 'attendance-center-my-records',
  myRequests: 'attendance-center-my-requests',
  myLeaveBalances: 'attendance-center-my-leave-balances',
  myMonthly: 'attendance-center-my-monthly',
  teamOverview: 'attendance-center-team-overview',
  teamSchedules: 'attendance-center-team-schedules',
  teamExceptions: 'attendance-center-team-exceptions',
  teamMonthly: 'attendance-center-team-monthly',
  workbench: 'attendance-center-workbench',
  approval: 'attendance-center-approval',
  records: 'attendance-center-records',
  dailyResults: 'attendance-center-daily-results',
  monthlySummaries: 'attendance-center-monthly-summaries',
  operationAudits: 'attendance-center-operation-audits',
  leaveRequests: 'attendance-center-leave-requests',
  overtimeRequests: 'attendance-center-overtime-requests',
  punchRequests: 'attendance-center-punch-requests',
  shifts: 'attendance-center-shifts',
  rules: 'attendance-center-rules',
  groups: 'attendance-center-groups',
  schedules: 'attendance-center-schedules',
  holidayPlans: 'attendance-center-holiday-plans',
  leaveBalances: 'attendance-center-leave-balances',
  leaveGrantPolicies: 'attendance-center-leave-grant-policies',
  settings: 'attendance-center-settings',
  workflowSettings: 'attendance-center-workflow-settings',
} as const;

export type PermissionResourceCode =
  (typeof PERMISSION_RESOURCE_CODES)[keyof typeof PERMISSION_RESOURCE_CODES];
